import { useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Textarea } from "../ui/textarea";
import { toast } from "sonner";
import { MessageSquare } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function SupportTab({ tickets, isLoading, getStatusBadge, formatDate, fetchAllData }) {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const updateTicketStatus = async (ticketId, status, response = null) => {
    try {
      const data = { status };
      if (response) data.response = response;
      await axios.patch(`${API}/portal/support/${ticketId}`, data);
      toast.success("Updated"); setReplyingTo(null); setReplyText(""); fetchAllData();
    } catch (e) { toast.error("Failed"); }
  };

  if (isLoading) return <div className="text-center py-12 text-zinc-500">Loading...</div>;
  if (tickets.length === 0) return <div className="text-center py-12 text-zinc-500 bg-white rounded-sm border">No tickets yet</div>;

  return (
    <div className="bg-white rounded-sm border border-zinc-200 overflow-hidden overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className="bg-zinc-50">
            <TableHead className="font-heading uppercase text-xs">Subject</TableHead>
            <TableHead className="font-heading uppercase text-xs">Customer</TableHead>
            <TableHead className="font-heading uppercase text-xs">Priority</TableHead>
            <TableHead className="font-heading uppercase text-xs">Status</TableHead>
            <TableHead className="font-heading uppercase text-xs">Date</TableHead>
            <TableHead className="font-heading uppercase text-xs">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tickets.map((t) => (
            <TableRow key={t.id}>
              <TableCell>
                <p className="font-medium">{t.subject}</p>
                <p className="text-sm text-zinc-500 truncate max-w-xs">{t.message}</p>
              </TableCell>
              <TableCell className="text-sm">{t.customer_email}</TableCell>
              <TableCell>
                <Badge className={t.priority === "urgent" ? "bg-red-100 text-red-800" : t.priority === "high" ? "bg-orange-100 text-orange-800" : "bg-gray-100 text-gray-800"}>
                  {t.priority}
                </Badge>
              </TableCell>
              <TableCell><Badge className={getStatusBadge(t.status)}>{t.status}</Badge></TableCell>
              <TableCell className="text-sm text-zinc-500">{formatDate(t.created_at)}</TableCell>
              <TableCell>
                <div className="flex gap-2">
                  <Dialog open={replyingTo === t.id} onOpenChange={(open) => setReplyingTo(open ? t.id : null)}>
                    <DialogTrigger asChild><Button variant="outline" size="sm"><MessageSquare className="h-4 w-4" /></Button></DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-heading uppercase">Reply</DialogTitle></DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="bg-zinc-50 p-3 rounded-sm">
                          <p className="text-sm font-medium">{t.subject}</p>
                          <p className="text-sm text-zinc-600 mt-1">{t.message}</p>
                        </div>
                        <Textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} placeholder="Your response..." rows={4} />
                        <Button onClick={() => updateTicketStatus(t.id, "in_progress", replyText)} className="w-full bg-blue-600 hover:bg-blue-700">Send Reply</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <Select value={t.status} onValueChange={(v) => updateTicketStatus(t.id, v)}>
                    <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">Open</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
